import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SearchableSelect } from "../components/ui/searchable-select";
import { AttachmentUploader } from "../components/Attachments";
import MultiSelect from "../components/MultiSelect";
import RichTextEditor from "../components/RichTextEditor";
import useFieldConfigs from "../hooks/useFieldConfigs";

export default function NewTicket() {
  const navigate = useNavigate();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [projects, setProjects] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const [fields, setFields] = useState([]);
  const [cf, setCf] = useState({});
  const [form, setForm] = useState({
    project_id: "",
    subject: "",
    description: "",
    reference_url: "",
    category: "",
    request_type: "bug_report",
    request_subtypes: [],
    priority: "",
    impact: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { fieldOn, fieldReq, subtypeReq } = useFieldConfigs();

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data.length === 1) setForm((f) => ({ ...f, project_id: r.data[0].id }));
    });
    api.get("/request-types", { params: { active_only: true } }).then((r) => {
      setRequestTypes(r.data);
      if (r.data.length && !r.data.some((t) => t.value === "bug_report")) {
        setForm((f) => ({ ...f, request_type: r.data[0].value }));
      }
    });
    api.get("/masters/category?active_only=true").then((r) => {
      setCategories(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, category: f.category || r.data[0].value }));
    });
    api.get("/masters/priority?active_only=true").then((r) => {
      setPriorities(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, priority: f.priority || r.data[0].value }));
    });
    api.get("/masters/impact?active_only=true").then((r) => {
      setImpacts(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, impact: f.impact || r.data[0].value }));
    });
    api.get("/custom-fields?active_only=true").then((r) => setFields(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setField = (k, v) => setCf((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.project_id) {
      setError("Please select a project.");
      return;
    }
    const plain = form.description.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (!plain) {
      setError("Please describe the issue.");
      return;
    }
    // Enforce required built-in fields
    if (fieldOn("reference_url") && fieldReq("reference_url") && !form.reference_url.trim()) {
      setError("Reference URL is required.");
      return;
    }
    if (fieldOn("attachments") && fieldReq("attachments") && attachments.length === 0) {
      setError("At least one attachment is required.");
      return;
    }
    if (fieldOn("request_type") && subtypeReq() && form.request_subtypes.length === 0) {
      const subs = (requestTypes.find((t) => t.value === form.request_type)?.sub_types || []).filter((s) => s.active);
      if (subs.length > 0) {
        setError("At least one sub-type is required.");
        return;
      }
    }
    // Enforce required custom fields
    for (const f of fields) {
      if (f.required && !cf[f.key]?.toString().trim()) {
        setError(`"${f.label}" is required.`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tickets", { ...form, attachments, custom_fields: cf });
      navigate(`/tickets/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="new-ticket-page">
      <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-3" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">New request</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Create a ticket</h1>
        <p className="text-sm text-slate-500 mt-1">Describe the issue or change you&apos;d like the team to take on.</p>
      </div>

      <form onSubmit={onSubmit} className="surface-card p-6 space-y-5" data-testid="new-ticket-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Project *</Label>
            {projects.length === 1 ? (
              <div
                className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
                data-testid="new-project-readonly"
              >
                {projects[0].name}
              </div>
            ) : (
              <SearchableSelect
                value={form.project_id}
                onValueChange={(v) => set("project_id", v)}
                options={projects.map((p) => ({ value: p.id, label: p.name }))}
                placeholder="Select project"
                testId="new-project-select"
              />
            )}
          </div>
          {fieldOn("request_type") && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Request type{fieldReq("request_type") && " *"}
              </Label>
              <SearchableSelect
                value={form.request_type}
                onValueChange={(v) => setForm((f) => ({ ...f, request_type: v, request_subtypes: [] }))}
                options={requestTypes.map((t) => ({ value: t.value, label: t.label }))}
                testId="new-type-select"
              />
              {(() => {
                const subs = (requestTypes.find((t) => t.value === form.request_type)?.sub_types || [])
                  .filter((s) => s.active);
                if (subs.length === 0) return null;
                return (
                  <div className="space-y-1.5 pt-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Sub-types{subtypeReq() && " *"}
                    </Label>
                    <MultiSelect
                      value={form.request_subtypes}
                      onChange={(v) => set("request_subtypes", v)}
                      options={subs.map((s) => ({ value: s.value, label: s.label }))}
                      placeholder={subtypeReq() ? "Select sub-types" : "Select sub-types (optional)"}
                      testId="new-subtype-select"
                    />
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject *</Label>
          <Input required value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Short, descriptive title…" data-testid="new-subject-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description *</Label>
          <RichTextEditor value={form.description} onChange={(html) => set("description", html)}
            placeholder="Describe the problem, steps to reproduce, what you expected to see…" testId="new-description-input" />
        </div>

        {fieldOn("reference_url") && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Reference URL{fieldReq("reference_url") && " *"}
            </Label>
            <Input type="url" value={form.reference_url} onChange={(e) => set("reference_url", e.target.value)}
              placeholder="https://… page where the change or problem was noticed" data-testid="new-url-input" />
            <p className="text-[11px] text-slate-400">Optional — the exact page/screen where you spotted the issue.</p>
          </div>
        )}

        {fieldOn("attachments") && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Attachments{fieldReq("attachments") ? " *" : " (optional)"}
            </Label>
            <AttachmentUploader value={attachments} onChange={setAttachments} />
            <p className="text-[11px] text-slate-400">Screenshots, mockups, PDFs or documents to help the team — you can add several.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fieldOn("category") && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Category{fieldReq("category") && " *"}
              </Label>
              <SearchableSelect
                value={form.category}
                onValueChange={(v) => set("category", v)}
                options={categories.map((m) => ({ value: m.value, label: m.label }))}
                testId="new-category-select"
              />
            </div>
          )}
          {fieldOn("priority") && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Priority{fieldReq("priority") && " *"}
              </Label>
              <SearchableSelect
                value={form.priority}
                onValueChange={(v) => set("priority", v)}
                options={priorities.map((m) => ({ value: m.value, label: m.label }))}
                testId="new-priority-select"
              />
            </div>
          )}
          {fieldOn("impact") && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Business impact{fieldReq("impact") && " *"}
              </Label>
              <SearchableSelect
                value={form.impact}
                onValueChange={(v) => set("impact", v)}
                options={impacts.map((m) => ({ value: m.value, label: m.label }))}
                testId="new-impact-select"
              />
            </div>
          )}
        </div>

        {/* Dynamic custom fields */}
        {fields.length > 0 && (
          <div className="space-y-4 pt-4 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Additional details</div>
            {fields.map((f) => (
              <div key={f.id} className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {f.label}{f.required && " *"}
                </Label>
                {f.type === "text" && (
                  <Input
                    value={cf[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.label}
                  />
                )}
                {f.type === "paragraph" && (
                  <textarea
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y min-h-[80px]"
                    value={cf[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.label}
                  />
                )}
                {f.type === "number" && (
                  <Input
                    type="number"
                    value={cf[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.label}
                  />
                )}
                {f.type === "date" && (
                  <Input
                    type="date"
                    value={cf[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                  />
                )}
                {f.type === "select" && (
                  <SearchableSelect
                    value={cf[f.key] || ""}
                    onValueChange={(v) => setField(f.key, v)}
                    options={(f.options || []).map((opt) => ({ value: opt, label: opt }))}
                    placeholder={`Select ${f.label}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="new-error">{error}</div>
        )}

        <div className="flex items-center justify-end gap-2 flex-wrap pt-2 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-slate-300" data-testid="new-cancel-button">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-submit-button">
            <FloppyDisk size={14} weight="regular" className="mr-1.5" /> {saving ? "Creating…" : "Create ticket"}
          </Button>
        </div>
      </form>
    </div>
  );
}
